export class TimeSync {
  private _timeServerURL: null | string = null;
  private _retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _positionAtZero: number = 0;
  private _abortController: AbortController = new AbortController();

  private readonly _resyncInterval: number = -1;

  constructor(resyncInterval: number) {
    this._resyncInterval = resyncInterval;
  }

  public setUrl(url: string): void {
    this._timeServerURL = url;

    if (this._retryTimeoutId) {
      clearTimeout(this._retryTimeoutId);
    }
    this._checkDrift();
  }

  public destroy(): void {
    this._abortController.abort();
    if (this._retryTimeoutId) {
      clearTimeout(this._retryTimeoutId);
    }
  }

  private async _checkDrift(): Promise<void> {
    if (!this._timeServerURL) {
      return;
    }
    try {
      const response: Response = await fetch(`${this._timeServerURL}?orig=doppio`, {
        cache: 'no-cache',
        signal: this._abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Could not get time sync response: ${response.status}`);
      }

      const reqEndTs: number = performance.now();
      let rtt: number = 0;
      const detailedTimings: PerformanceResourceTiming | undefined = performance.getEntriesByName(`${this._timeServerURL}/time`)[0] as
        | PerformanceResourceTiming
        | undefined;
      const { requestStart, responseStart, responseEnd } = detailedTimings || {};

      if (requestStart && responseStart && responseEnd) {
        rtt = (responseStart - requestStart) / 2 + (responseEnd - responseStart);
        performance.clearResourceTimings();
      }

      const responseTime: number = Number(await response.text()) * 1000;
      this._positionAtZero = responseTime + rtt - reqEndTs;

      if (this._retryTimeoutId) {
        clearTimeout(this._retryTimeoutId);
      }

      if (this._resyncInterval > -1) {
        this._retryTimeoutId = setTimeout(() => this._checkDrift(), this._resyncInterval);
      }
    } catch (e) {
      if (this._retryTimeoutId) {
        clearTimeout(this._retryTimeoutId);
      }
      if ((e as Error).name === 'AbortController') {
        return;
      }

      this._retryTimeoutId = setTimeout(() => this._checkDrift(), 1000);
    }
  }

  get currentCorrectedTime(): number {
    return this._positionAtZero > 0 ? Math.round(this._positionAtZero + performance.now()) : Date.now();
  }
}
